import { Button, MessageDialog } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FixedPageHeader } from 'Components/Layout';
import { hasValue } from 'Lib/Any';
import { combine } from 'Lib/Hooks/Selectors';
import { IconLock, IconRestore } from 'Lib/Icons';
import deepEquals from 'Lib/Object/deepEquals';
import { usePreventWindowUnload } from 'Lib/preventWindowUnload';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { SnapshotDialog } from '../WorksheetSnapshots/SnapshotDialog';
import WorksheetStatus from '../WorksheetStatus';
import { IWithWorksheetInjectedProps } from '../types';

const enableSnapshotPrototype = process.env.REACT_APP_ENABLE_SNAPSHOTS_PROTOTYPE === 'true';

let FixedWorksheetHeader: React.FC<IWithWorksheetInjectedProps> = ({ createWorksheetSelector }) => {
  const {
    dirty,
    readonly: { locked, lockedMessage },
  } = useSelector((s) => s.worksheetEditor.status, deepEquals);

  const saving = useSelector(
    combine(
      [
        (s) => s.worksheetEditor.status.saving,
        // Additional actions that should be considered as "saving" actions
        (s) => s.psdSieveAnalysis.refreshSieves.status.submitting,
      ],
      (...savingFlags) => savingFlags.some((f) => f)
    )
  );

  const [showSnapshotDialog, setShowSnapshotDialog] = useState(false);
  const snapshotId = useSelector((s) => s.worksheetEditor.snapshotPreview?.id);
  const header = useSelector(createWorksheetSelector!('header', false), deepEquals);

  const methodCaption = useSelector(createWorksheetSelector!('type.MethodCaption', false));

  // Prevent closing of browser window if unsaved changes
  usePreventWindowUnload(saving || dirty);

  return (
    <FixedPageHeader
      pageIdentifier={header.Sample && header.Sample.SampleId}
      pageCaption={
        <>
          <span className="FixedPageHeader-StatusIcon">
            <WorksheetStatus status={header} />
          </span>
          <span>{methodCaption}</span>
        </>
      }
      pageStatus={
        hasValue(snapshotId) ? (
          'Snapshot Preview'
        ) : saving ? (
          'Saving...'
        ) : locked ? (
          <MessageDialog messageTitle="Worksheet Locked" messageText={lockedMessage} alwaysEnabled>
            <Button subtle noPadding alwaysEnabled>
              <IconLock />
              Read Only
            </Button>
          </MessageDialog>
        ) : (
          'Saved'
        )
      }
      pageProcessing={saving || dirty}
      pageAdditionalAction={
        enableSnapshotPrototype &&
        !hasValue(snapshotId) && (
          <span>
            <Button subtle noPadding onClick={() => setShowSnapshotDialog(true)}>
              <IconRestore />
            </Button>

            <SnapshotDialog
              visible={showSnapshotDialog}
              onClose={() => setShowSnapshotDialog(false)}
              namePrefix={undefined}
            />
          </span>
        )
      }
    />
  );
};
FixedWorksheetHeader = withInjectedProps(FixedWorksheetHeader);

export { FixedWorksheetHeader };
export default FixedWorksheetHeader;
